import { useState } from 'react';
import {
  Color, DEFAULT_WORKING_DAY, Size, WORKING_PATTERN_DAY_ORDER, clockLabel, t, timeOfDayChoices, weekdayLabel, workingPatternPath, workingPatternProblems,
  type WorkingPatternDraft, type WorkingPatternSection,
} from '@bms2/shared';
import { Button, Checkbox, DatePicker, Select } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { post } from '../records/api';
import styles from './HrPages.module.css';

const STARTS = timeOfDayChoices().map((value) => ({ value, label: clockLabel(value) }));
const ENDS = timeOfDayChoices(true).map((value) => ({ value, label: clockLabel(value) }));

/**
 * Edit Working Pattern (piece 34, *The working pattern*; decision 12850): the
 * day the pattern takes effect and, for each day of the week, whether it is
 * worked with its start and end. 🔒 Drawn through the Form frame, and the save
 * writes a new dated row; the earlier pattern stays as the history.
 */
export function WorkingPatternWindow({ apiBaseUrl = '', employmentId, start, onClose, onSaved }: {
  apiBaseUrl?: string;
  employmentId: string;
  start: WorkingPatternDraft;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<WorkingPatternDraft>(start);
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (next: WorkingPatternDraft) => {
    setTyped(true);
    setDraft(next);
  };
  const setDay = (weekday: number, value: WorkingPatternDraft['days'][number]) => change({ ...draft, days: draft.days.map((day, index) => (index === weekday ? value : day)) });
  const problems = workingPatternProblems(draft);
  const save = async () => {
    const answer = await post<{ workingPatternId: string }>(workingPatternPath(employmentId), draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.pattern.title')}
      width="wide"
      dataForm="working-pattern"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.pattern.act'), onAct: () => save(), disabled: problems.length > 0 || !typed, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-working-pattern={employmentId}>
        <p className={styles.meta}>{t('hr.pattern.lead')}</p>
        <DatePicker size={Size.Medium} label={t('hr.pattern.effectiveFrom')} value={draft.effectiveFrom} onChange={(effectiveFrom) => change({ ...draft, effectiveFrom })} required name="workingPatternStart" />
        {WORKING_PATTERN_DAY_ORDER.map((weekday) => {
          const day = draft.days[weekday] ?? null;
          return (
            <div key={weekday} className={styles.controls} data-working-pattern-day={weekday}>
              <Checkbox label={weekdayLabel(weekday)} checked={day !== null} onChange={(on) => setDay(weekday, on ? { ...DEFAULT_WORKING_DAY } : null)} name={`working-pattern-day-${weekday}`} />
              {day !== null ? (
                <>
                  <Select size={Size.Small} label={t('hr.pattern.starts')} value={day.startsAt} onChange={(startsAt) => setDay(weekday, { ...day, startsAt })} options={STARTS} dataAttribute={{ 'data-working-pattern-starts': String(weekday) }} required />
                  <Select size={Size.Small} label={t('hr.pattern.ends')} value={day.endsAt} onChange={(endsAt) => setDay(weekday, { ...day, endsAt })} options={ENDS} dataAttribute={{ 'data-working-pattern-ends': String(weekday) }} required />
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </FormFrame>
  );
}

/**
 * The Job section's control (decision 12850): Edit Working Pattern for HR with
 * the hire permission and a manager above the job, on a job that has not ended.
 * The file sends the section only to those, so the button draws where the
 * route answers.
 */
export function WorkingPatternControls({ apiBaseUrl, employmentId, section, onSaved }: { apiBaseUrl: string; employmentId: string; section: WorkingPatternSection; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!section.mayEdit) return null;
  return (
    <div className={styles.fields} data-employee-working-pattern={employmentId}>
      <div className={styles.controls}>
        <span data-working-pattern-edit="open"><Button size={Size.Small} color={Color.Navy} text={[t('hr.pattern.edit')]} actions={[() => { setSaved(false); setEditing(true); }]} /></span>
      </div>
      {saved ? <p className={styles.meta} role="status" data-working-pattern-saved="true">{t('hr.pattern.saved')}</p> : null}
      {/* 🔨 Mounted only while open and keyed to the job, so a reopening reads the pattern in force afresh. */}
      {editing ? (
        <WorkingPatternWindow
          key={`pattern-${employmentId}`}
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          start={section.start}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSaved(true);
            onSaved();
          }}
        />
      ) : null}
    </div>
  );
}
